/**
 * Tier - 요금제 모델
 * 
 * Super Admin이 관리하는 구독 요금제 정의
 * Firestore Path: /admin/tiers/{tierId}
 */

export interface Tier {
    id: string;                    // 'FREE', 'BASIC', 'PREMIUM' 등
    name: string;                  // 표시 이름
    description?: string;          // 요금제 설명

    // 가격 정보
    price: {
        monthly: number;           // 월간 가격 (JPY)
        yearly: number;            // 연간 가격 (JPY)
        currency: 'JPY' | 'KRW' | 'USD';
    };

    // 제한 사항
    limits: {
        storageBytes: number;      // 저장소 용량 (bytes) - 0 = 무제한
        monthlyDownloadBytes: number; // 월간 다운로드 (bytes) - 0 = 무제한
        memberCount: number;       // 최대 멤버 수 - 0 = 무제한
        channelCount: number;      // 최대 채널 수 - 0 = 무제한
        botCount: number;          // 최대 봇 수 - 0 = 무제한
    };

    // 기능 활성화
    features: {
        customBranding: boolean;   // 커스텀 브랜딩
        apiAccess: boolean;        // API 접근
        prioritySupport: boolean;  // 우선 지원
        analytics: boolean;        // 분석 기능
    };

    // 메타데이터
    isActive: boolean;             // 활성화 여부
    displayOrder: number;          // 표시 순서
    createdAt: Date;
    updatedAt: Date;
}

// 기본 요금제 템플릿
export const DEFAULT_TIERS: Omit<Tier, 'createdAt' | 'updatedAt'>[] = [
    {
        id: 'FREE',
        name: '무료',
        description: '개인 사용자를 위한 기본 플랜',
        price: { monthly: 0, yearly: 0, currency: 'JPY' },
        limits: {
            storageBytes: 1 * 1024 * 1024 * 1024,      // 1GB
            monthlyDownloadBytes: 5 * 1024 * 1024 * 1024, // 5GB
            memberCount: 5,
            channelCount: 3,
            botCount: 1,
        },
        features: {
            customBranding: false,
            apiAccess: false,
            prioritySupport: false,
            analytics: false,
        },
        isActive: true,
        displayOrder: 0,
    },
    {
        id: 'BASIC',
        name: 'ベーシック',
        description: '소규모 팀을 위한 기본 플랜',
        price: { monthly: 2980, yearly: 29800, currency: 'JPY' },
        limits: {
            storageBytes: 10 * 1024 * 1024 * 1024,     // 10GB
            monthlyDownloadBytes: 50 * 1024 * 1024 * 1024, // 50GB
            memberCount: 20,
            channelCount: 10,
            botCount: 5,
        },
        features: {
            customBranding: false,
            apiAccess: true,
            prioritySupport: false,
            analytics: true,
        },
        isActive: true,
        displayOrder: 1,
    },
    {
        id: 'PREMIUM',
        name: 'プレミアム',
        description: '대규모 팀을 위한 프리미엄 플랜',
        price: { monthly: 9800, yearly: 98000, currency: 'JPY' },
        limits: {
            storageBytes: 100 * 1024 * 1024 * 1024,    // 100GB
            monthlyDownloadBytes: 500 * 1024 * 1024 * 1024, // 500GB
            memberCount: 0,  // 무제한
            channelCount: 0, // 무제한
            botCount: 0,     // 무제한
        },
        features: {
            customBranding: true,
            apiAccess: true,
            prioritySupport: true,
            analytics: true,
        },
        isActive: true,
        displayOrder: 2,
    },
];

// Admin 네임스페이스
export const TIERS_COL = 'admin/tiers/items';

// 용량 포맷팅 유틸리티
export function formatBytes(bytes: number): string {
    if (bytes === 0) return '無制限';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

export function formatCount(count: number): string {
    return count === 0 ? '無制限' : `${count}`;
}

export function formatPrice(price: number, currency: string): string {
    if (price === 0) return '無料';
    return `¥${price.toLocaleString()}`;
}
